import React, { useEffect, useState } from 'react';
import { Table, Divider, Tag } from 'rsuite';
import { AxiosError } from 'axios';
import PageContent from '@/components/PageContent';
import TablePagination from '@/components/TablePagination';
import { BaseErrorModel, PaginationFilterRequest, PaginationFilterResult } from '@/models/core.model';
import {
  EnergyHubCreditTransaction, EnergyHubCreditTransactionType, EnergyHubCreditTransactionTypeList, formatHkd,
} from '@/models/energy-hub.model';
import { getPagedEnergyHubCreditTransactions } from '@/services/energy-hub.service';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { jsDateToZone } from '@/utils/date-time';

const { Column, HeaderCell, Cell } = Table;

const typeColor: Record<number, string> = {
  [EnergyHubCreditTransactionType.TopUp]: 'green',
  [EnergyHubCreditTransactionType.CheckInFee]: 'blue',
  [EnergyHubCreditTransactionType.PurchaseFee]: 'violet',
  [EnergyHubCreditTransactionType.Refund]: 'red',
  [EnergyHubCreditTransactionType.Adjustment]: 'orange',
};

interface Props {
  energyHubId: number;
  reloadKey?: number;
}

const EnergyHubCreditLedger: React.FC<Props> = ({ energyHubId, reloadKey }) => {
  const [filterReq, setFilterReq] = useState<PaginationFilterRequest>(new PaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<EnergyHubCreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [filterReq, energyHubId, reloadKey]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getPagedEnergyHubCreditTransactions(energyHubId, filterReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ ...pre, pageNumber: result.pageNumber, totalRecords: result.totalRecords, pageSize: result.pageSize }));
      }
    } catch (err) {
      setItems([]);
      const em = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching credit ledger: ${em?.errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContent>
      <Table height={500} data={items} loading={loading}>
        <Column flexGrow={0.5}>
          <HeaderCell>Id</HeaderCell>
          <Cell dataKey="id" />
        </Column>
        <Column flexGrow={1} align="center">
          <HeaderCell>Type</HeaderCell>
          <Cell>
            {(r: EnergyHubCreditTransaction) => (
              <Tag color={typeColor[r.type] as any}>
                {EnergyHubCreditTransactionTypeList.find(x => x.value == r.type)?.label ?? r.type}
              </Tag>
            )}
          </Cell>
        </Column>
        <Column flexGrow={0.8} align="right">
          <HeaderCell>Amount</HeaderCell>
          <Cell>
            {(r: EnergyHubCreditTransaction) => (
              <span style={{ color: r.amount > 0 ? '#2db85d' : r.amount < 0 ? '#e55' : '#888', fontWeight: 600 }}>
                {r.amount > 0 ? '+' : ''}{formatHkd(r.amount)}
              </span>
            )}
          </Cell>
        </Column>
        <Column flexGrow={0.8} align="right">
          <HeaderCell>Waived</HeaderCell>
          <Cell>{(r: EnergyHubCreditTransaction) => (r.waivedAmount ? formatHkd(r.waivedAmount) : '-')}</Cell>
        </Column>
        <Column flexGrow={0.9}>
          <HeaderCell>Source</HeaderCell>
          <Cell>
            {(r: EnergyHubCreditTransaction) => r.visitId ? `Visit #${r.visitId}` : r.receiptId ? `Receipt #${r.receiptId}` : '-'}
          </Cell>
        </Column>
        <Column flexGrow={2.5} fullText>
          <HeaderCell>Note</HeaderCell>
          <Cell dataKey="note" />
        </Column>
        <Column flexGrow={0.7}>
          <HeaderCell>Created By</HeaderCell>
          <Cell dataKey="createdBy" />
        </Column>
        <Column flexGrow={1.2}>
          <HeaderCell>Created At</HeaderCell>
          <Cell fullText>{(r: EnergyHubCreditTransaction) => jsDateToZone(r.createdAt)}</Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={val => setFilterReq(pre => ({ ...pre, pageNumber: val }))}
        onChangeLimit={val => setFilterReq(pre => ({ ...pre, pageSize: val }))}
      />
    </PageContent>
  );
};

export default EnergyHubCreditLedger;
